/* eslint-disable max-statements */

import { Button, Checkbox, Grid, Group, LoadingOverlay, ModalCloseButton, ModalHeader, ModalTitle, Stack, Text } from '@mantine/core';
import { cloneDeep } from 'lodash';
import { Dispatch, useEffect, useRef, useState } from 'react';

import { Batch, Customer, DeclarationGoods, IRiskAnalysisGoodsRef, IRiskAnalysisUpdateFields, QueueList } from '@e-origin/shared';

import { TextInput } from '@components';
import { RISK_VALIDATION_REQUIRED } from '@constants';
import { FormatValueTypes } from '@enums';
import { useCustomer } from '@hooks/customer';
import { useDeclarant } from '@hooks/declarant';
import { retrieveDeclarationsRefsByImpact, useDeclaration } from '@hooks/declaration';
import { queryClient } from '@hooks/queryClient';
import { useUserRiskAnalysisParameters } from '@hooks/user';
import { useSettings } from '@hooks/useSettings';
import { formatValue } from '@utils';
import { riskAnalysisService, useDeclarationService } from '../../services';
import { RiskAnalysisDescription } from './RiskAnalysisDescription';
import { RiskAnalysisHSCode } from './RiskAnalysisHSCode';
import { RiskAnalysisOthers } from './RiskAnalysisOthers';
import { RiskAnalysisPrice } from './RiskAnalysisPrice';

interface RiskAnalysisProps {
  onHide(): void;
  batch?: Batch;
  goodDetailsId?: string;
  declarationId?: string;
}

export enum ValidateAllStateEnum {
  ALL_SELECTED = 'ALL_SELECTED',
  SOME_SELECTED = 'SOME_SELECTED',
  UNSELECTED = 'UNSELECTED',
}

export const RiskAnalysis: React.FC<RiskAnalysisProps> = ({ batch, goodDetailsId, declarationId, onHide }) => {
  const { data: settings } = useSettings();
  const [declarationQueueList, setDeclarationQueueList] = useState<QueueList<IRiskAnalysisGoodsRef>>(null);
  let goodsForCurrentDeclaration: DeclarationGoods[] = [];
  const { data: declaration } = useDeclaration(declarationQueueList?.top?.declarationId || declarationId);
  const [goodDetails, setGoodDetails]: [DeclarationGoods, Dispatch<DeclarationGoods>] = useState(null);
  const goodDetailsOriginal = useRef<DeclarationGoods>(null);

  const { saveMultipleGoods } = useDeclarationService();

  const { data: customer } = useCustomer((declaration?.messageInfo.batch as unknown as Batch)?.customer);
  const { data: userRiskAnalysisParameters } = useUserRiskAnalysisParameters(
    declaration?.createdBy.toString(),
    settings.isForwarder && !!declaration?.createdBy,
  );

  const [minimumRate, setMinimumRate] = useState<number>();

  const aiModuleMustRunAfterSave = useRef<boolean>(false);

  const { data: declarant } = useDeclarant();

  const [validateAllCheck, setValidateAllCheck] = useState<ValidateAllStateEnum>(null);

  const [totalDeclaredValue, setTotalDeclaredValue] = useState<string>('0');
  const [reasonableTotalValue, setReasonableTotalValue] = useState<string>('0');
  const [impact, setImpact] = useState<string>('0');

  const data = useRef<IRiskAnalysisUpdateFields>(null);

  useEffect(() => {
    if (!customer) return;

    let rate = customer?.evaluationMethods?.evalMethodIMADBStatisticalValue?.minimumRate;

    if (settings.isForwarder) {
      const userParameters = userRiskAnalysisParameters?.customerEvaluationMethods?.find(
        (method) => method.customer === batch?.customer || method.customer === (declaration?.customer as unknown as Customer)._id,
      );
      if (userParameters?.evaluationMethods?.evalMethodIMADBStatisticalValue?.minimumRate !== undefined) {
        rate = userParameters.evaluationMethods.evalMethodIMADBStatisticalValue.minimumRate;
      }
    }

    setMinimumRate(rate);
  }, [customer, settings, userRiskAnalysisParameters]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.stopPropagation();
        return;
      }
    };

    document.addEventListener('wheel', handleWheel, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener('wheel', handleWheel, true);
    };
  }, []);

  const processData = (d) => {
    if (!goodDetails) {
      data.current = null;
      return;
    }

    data.current = { ...data.current, ...d };

    Object.keys(data.current || {}).forEach((key) => {
      if (data.current[key] === undefined) {
        delete data.current[key];
      }
    });

    setGoodDetails(riskAnalysisService.computeUpdatedDeclarationGoods(minimumRate, goodDetails, data.current));
  };

  const resetModal = () => {
    setValidateAllCheck(null);
    aiModuleMustRunAfterSave.current = false;
    data.current = {} as IRiskAnalysisUpdateFields;
  };

  const fetchDeclarationsRefsWithAnomalies = (startWithLastItem = false) => {
    retrieveDeclarationsRefsByImpact({ batchId: batch?._id }).then(async (refs) => {
      if (!refs?.length) {
        return onHide();
      }
      const newQueue = new QueueList<IRiskAnalysisGoodsRef>(refs || []);
      if (startWithLastItem) {
        newQueue.empty();
        newQueue.backward();
      }
      setDeclarationQueueList(newQueue);
    });
  };

  useEffect(() => {
    if (batch && ((declaration?.messageInfo.batch as unknown as Batch)?._id === batch?._id || declaration?.messageInfo.batch === batch?._id)) {
      const goodItem = declaration.goodsShipmentItems.find((good) => good._id === declarationQueueList.top?.goodsShipmentItemId);
      setGoodDetails(goodItem);
      goodDetailsOriginal.current = cloneDeep(goodItem);
    } else if (goodDetailsId && declaration) {
      const goodItem = declaration.goodsShipmentItems.find((good) => good._id === goodDetailsId);
      setGoodDetails(goodItem);
      goodDetailsOriginal.current = cloneDeep(goodItem);
    }

    if (declaration) {
      const goodsIdsForCurrentDeclaration = (declarationQueueList?.filter((item) => item.declarationId === declaration._id) || []).map(
        (item) => item.goodsShipmentItemId,
      );
      const shouldFilter = !!goodDetails?.evalMethodAiHsCode?.suggestedHsCode;
      goodsForCurrentDeclaration = declaration.goodsShipmentItems.filter((item) => (shouldFilter ? goodsIdsForCurrentDeclaration.includes(item._id) : true));
      let totalDeclaredValueSum = 0;
      let reasonableTotalValueSum = 0;
      let impactSum = 0;
      goodsForCurrentDeclaration.forEach((item) => {
        totalDeclaredValueSum += item.dutiesAndTaxes.items[0].taxBase[0].amount || 0;
        reasonableTotalValueSum += item.suggestedValues?.totalValue || 0;
        impactSum += item.suggestedValues?.additionalDuties || 0;
      });
      setTotalDeclaredValue(formatValue(totalDeclaredValueSum || 0, FormatValueTypes.TOTAL_VALUE));
      setReasonableTotalValue(formatValue(reasonableTotalValueSum || 0, FormatValueTypes.TOTAL_VALUE));
      setImpact(formatValue(impactSum || 0, FormatValueTypes.TOTAL_VALUE));
    }
  }, [declaration, declarationQueueList]);

  useEffect(() => {
    if (!goodDetails) {
      return;
    }

    const isValidatedDescriptionCheckDisabled = riskAnalysisService.evalMethodDescriptionCheckDisabled(goodDetailsOriginal.current);
    const isValidatedValueCheckDisabled = riskAnalysisService.evalMethodWebValueCheckDisabled(goodDetails);

    const countSelected =
      Number(!!goodDetails.evalMethodDescription?.validatedDescriptionCheck || isValidatedDescriptionCheckDisabled) +
      Number(!!goodDetails.evalMethodAiHsCode?.validatedHSCodeCheck) +
      Number(!!goodDetails.evalMethodWebValue?.validatedValueCheck || isValidatedValueCheckDisabled);

    if (countSelected === RISK_VALIDATION_REQUIRED) {
      setValidateAllCheck(ValidateAllStateEnum.ALL_SELECTED);
    } else if (countSelected === 0) {
      setValidateAllCheck(ValidateAllStateEnum.UNSELECTED);
    } else {
      setValidateAllCheck(ValidateAllStateEnum.SOME_SELECTED);
    }
  }, [goodDetails]);

  const [isLoading, setIsLoading] = useState(false);

  const save = async (updatedGoods: DeclarationGoods[], runAiAfterSave?: boolean, newPriceSaved?: number) => {
    if (runAiAfterSave) {
      setIsLoading(true);
    }

    await saveMultipleGoods({
      declaration,
      updatedGoods,
      aiModuleMustRunAfterSave: runAiAfterSave,
      newPriceSaved,
      // workaround: don't change the store's state of declaration when saving, except when we run the ai module
      storeSavedDeclaration: runAiAfterSave || !batch,
    });
    if (runAiAfterSave) {
      setIsLoading(false);
    }
  };

  const submit = async () => {
    const otherItemsSameUrl =
      declaration.goodsShipmentItems
        ?.filter((item) => item._id !== goodDetails._id && item.webScraping?.webUrl === goodDetails.webScraping.webUrl)
        .map((item: any) => {
          if (data.current) {
            return riskAnalysisService.computeUpdatedDeclarationGoods(minimumRate, item, data.current);
          }
          return item;
        }) || [];

    await save([goodDetails, ...otherItemsSameUrl], aiModuleMustRunAfterSave.current, data.current.newPriceSaved);

    if ((!declarant?.risksCalculation?.aiModuleEnabled || !aiModuleMustRunAfterSave.current) && !batch) {
      onHide();
    }
  };

  useEffect(() => {
    if (batch?._id) {
      fetchDeclarationsRefsWithAnomalies();
    }
  }, []);

  const getPreviousGood = () => {
    if (declarationQueueList.total - declarationQueueList.length === 0) {
      return fetchDeclarationsRefsWithAnomalies(true);
    }
    declarationQueueList?.backward();
    setDeclarationQueueList(Object.assign(new QueueList(), declarationQueueList));
  };

  const getNextGood = () => {
    declarationQueueList?.forward();
    setDeclarationQueueList(Object.assign(new QueueList(), declarationQueueList));
    if (!declarationQueueList.top) {
      return fetchDeclarationsRefsWithAnomalies();
    }
  };

  const applyAndGoToNext = async (direction: -1 | 1 = 1) => {
    queryClient.invalidateQueries({ queryKey: ['declaration'] });
    await submit();
    resetModal();
    if (direction === 1) {
      getNextGood();
    } else {
      getPreviousGood();
    }
  };

  const runAiModule = async () => {
    await save([riskAnalysisService.computeUpdatedDeclarationGoods(minimumRate, goodDetails, data.current)], true);
    queryClient.invalidateQueries({ queryKey: ['goodsItemPredictedDistances', goodDetails._id] });
  };

  return (
    <>
      {goodDetails && (
        <>
          <LoadingOverlay visible={isLoading} zIndex={9999} />
          <ModalHeader p="lg">
            <Stack gap="xs">
              <ModalTitle>
                Risk Analysis <b>{batch?.name || ''}</b>
              </ModalTitle>
              <Text fw="bold">HS Code {goodDetails.commodityCode?.hsCode}</Text>
              {batch && (
                <span>
                  {declarationQueueList.total - declarationQueueList.length + 1} / {declarationQueueList.total}
                </span>
              )}
            </Stack>

            <Group justify="space-between" align="center" grow>
              <TextInput name="totalDeclaredValue" label="Total declared value" value={totalDeclaredValue} disabled={true} bg={'#f6f9ff'} />
              <TextInput name="reasonableTotalValue" label="Reasonable total value" value={reasonableTotalValue} disabled={true} bg={'#f6f9ff'} />
              <TextInput name="impact" label="Impact" value={impact} disabled={true} bg={'#f6f9ff'} />
            </Group>

            <Group>
              <Group justify="flex-end">
                <Checkbox
                  data-pw="validate-all-checkbox"
                  checked={validateAllCheck === ValidateAllStateEnum.ALL_SELECTED}
                  onChange={(event) => {
                    setValidateAllCheck(event.currentTarget.checked ? ValidateAllStateEnum.ALL_SELECTED : ValidateAllStateEnum.UNSELECTED);
                  }}
                  label="Validate all"
                  mr="md"
                />
                {batch ? (
                  <>
                    <Button key="prev-btn" type="button" onClick={async () => applyAndGoToNext(-1)} variant="filled" size="lg">
                      Previous
                    </Button>
                    <Button data-pw="next-btn" key="next-btn" type="button" onClick={async () => applyAndGoToNext(1)} variant="filled" size="lg">
                      {batch ? 'Next' : 'Apply'}
                    </Button>
                  </>
                ) : (
                  <Button data-pw="apply-btn" key="next-btn" type="button" onClick={submit} variant="filled" size="lg">
                    {'Apply'}
                  </Button>
                )}
              </Group>
              <ModalCloseButton data-pw="close-btn" />
            </Group>
          </ModalHeader>

          <Grid mt="xl">
            <Grid.Col span={6}>
              <RiskAnalysisDescription
                declaration={declaration}
                goodDetails={goodDetails}
                goodDetailsOriginal={goodDetailsOriginal.current}
                update={processData}
                runAiModule={runAiModule}
                validate={validateAllCheck}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <RiskAnalysisHSCode
                declaration={declaration}
                goodDetails={goodDetails}
                goodDetailsOriginal={goodDetailsOriginal.current}
                update={processData}
                validate={validateAllCheck}
              />
            </Grid.Col>
          </Grid>

          <Grid mb="xl">
            <Grid.Col span={6}>
              <RiskAnalysisPrice declaration={declaration} goodDetails={goodDetails} update={processData} validate={validateAllCheck} />
            </Grid.Col>
            <Grid.Col span={6}>
              <RiskAnalysisOthers goodDetails={goodDetails} />
            </Grid.Col>
          </Grid>
        </>
      )}
    </>
  );
};
